import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, FileDown, ArrowLeft, Save, Sparkles, ShieldCheck, Radio, Camera, Trophy, Newspaper, X, Eye, EyeOff, Pencil, RotateCcw, Link2, Loader2, Image as ImageIcon, Video, AlertTriangle, UserCheck } from "lucide-react";
import { MedalCard } from "@/components/MedalCard";
import { EntityLinksManager } from "@/components/EntityLinks";
import { AISourceSearchButton } from "@/components/AISourceSearchButton";
import { PalmaresAIOrganizer } from "@/components/PalmaresAIOrganizer";
import PhotoUploader from "@/components/PhotoUploader";
import AthleteMediaManager from "@/components/AthleteMediaManager";
import { showError } from "@/lib/errors";
import { AIAssistantPanel } from "@/components/AIAssistantPanel";

const EMPTY_ACH = {
  title: "", year: "", month: "", discipline: "", competition: "", place: "",
  category: "", age_class: "", belt: "", weight: "", medal: "", rank: "", level: "",
  result_type: "declare", source_url: "", photo_url: "", personal_comment: "",
  // V11.6 — Multi-disciplines
  sport_family: "", is_major_title: false, is_collective: false,
  team_name: "", opponent: "", match_outcome: "",
  time_performance: "", distance: "", crew_format: "",
};

const MONTHS = [
  { v: "", label: "— (année seule)" },
  { v: "1", label: "janvier" }, { v: "2", label: "février" }, { v: "3", label: "mars" },
  { v: "4", label: "avril" }, { v: "5", label: "mai" }, { v: "6", label: "juin" },
  { v: "7", label: "juillet" }, { v: "8", label: "août" }, { v: "9", label: "septembre" },
  { v: "10", label: "octobre" }, { v: "11", label: "novembre" }, { v: "12", label: "décembre" },
];

function AchievementInlineForm({ athleteId, disciplines, initialValue, editIndex, onSaved, onCancel }) {
  const isEdit = editIndex !== undefined && editIndex !== null;
  const buildInitial = () => {
    if (!initialValue) return { ...EMPTY_ACH };
    return {
      ...EMPTY_ACH,
      ...Object.fromEntries(Object.entries(initialValue).map(([k, v]) => {
        // Booléens : garde tel quel
        if (typeof v === "boolean") return [k, v];
        return [k, v == null ? "" : v];
      })),
    };
  };
  const [f, setF] = useState(buildInitial());
  const [saving, setSaving] = useState(false);
  const [familyMap, setFamilyMap] = useState({}); // discipline_slug -> family
  // Re-sync when changing the item being edited
  useEffect(() => { setF(buildInitial()); /* eslint-disable-next-line */ }, [editIndex, initialValue?.title, initialValue?.year]);

  // V11.6 — Charge le mapping discipline→famille (une seule fois)
  useEffect(() => {
    let cancel = false;
    api.get("/palmares/families").then(({ data }) => {
      if (!cancel && data?.discipline_to_family) setFamilyMap(data.discipline_to_family);
    }).catch(() => {});
    return () => { cancel = true; };
  }, []);

  // Famille sportive dérivée de la discipline
  const family = f.discipline ? (familyMap[f.discipline] || "generic") : "generic";
  const isCombat = family === "combat";
  const isTeam = family === "team";
  const isTimed = family === "timed";
  const isVaa = family === "vaa";
  const isStrength = family === "strength";
  const isRacket = family === "racket";

  const upd = (patch) => setF((c) => ({ ...c, ...patch }));
  const reset = () => setF(isEdit ? buildInitial() : { ...EMPTY_ACH });

  const submit = async () => {
    if (!f.title.trim()) { toast.error("Le titre est obligatoire."); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        year: f.year ? parseInt(f.year) : null,
        month: f.month ? parseInt(f.month) : null,
        rank: f.rank ? parseInt(f.rank) : null,
        discipline: f.discipline || null,
        sport_family: family || null,
        competition: f.competition || null,
        place: f.place || null,
        category: f.category || null,
        age_class: f.age_class || null,
        belt: f.belt || null,
        weight: f.weight || null,
        medal: f.medal || null,
        level: f.level || null,
        source_url: f.source_url || null,
        photo_url: f.photo_url || null,
        personal_comment: f.personal_comment || null,
        team_name: f.team_name || null,
        opponent: f.opponent || null,
        match_outcome: f.match_outcome || null,
        time_performance: f.time_performance || null,
        distance: f.distance || null,
        crew_format: f.crew_format || null,
        is_major_title: !!f.is_major_title,
        is_collective: !!f.is_collective,
      };
      let data;
      if (isEdit) {
        ({ data } = await api.patch(`/athletes/${athleteId}/list/achievements/${editIndex}`, payload));
        toast.success("Palmarès modifié.");
      } else {
        ({ data } = await api.post(`/athletes/${athleteId}/quick-add/achievement`, payload));
        toast.success("Palmarès ajouté.");
      }
      onSaved(data);
    } catch (e) { showError(e); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-4 border-2 border-[hsl(var(--aito-lagoon))]/40 bg-secondary/30" data-testid="achievement-inline-form">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-display font-semibold text-sm">
          {isEdit ? "Modifier ce résultat / palmarès" : "Ajouter un résultat / palmarès"}
        </h4>
        <div className="flex items-center gap-2">
          {f.discipline && (
            <Badge variant="outline" className="text-[10px] capitalize" data-testid="achievement-family-badge">
              {family === "combat" ? "Sport de combat" :
               family === "team" ? "Sport collectif" :
               family === "timed" ? "Chronométré" :
               family === "vaa" ? "Va'a" :
               family === "strength" ? "Sport de force" :
               family === "score_judge" ? "Score / Jugement" :
               family === "racket" ? "Sport de raquette" :
               family === "shooting" ? "Tir" : "Générique"}
            </Badge>
          )}
          <Button variant="ghost" size="icon" onClick={onCancel} data-testid="achievement-form-close"><X className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {/* --- Socle commun --- */}
        <div className="sm:col-span-2">
          <label className="text-xs">Titre <span className="text-destructive">*</span></label>
          <Input data-testid="achievement-input-title" value={f.title} onChange={(e) => upd({ title: e.target.value })} placeholder="Nom court du résultat" />
        </div>
        <div><label className="text-xs">Année</label><Input data-testid="achievement-input-year" type="number" value={f.year} onChange={(e) => upd({ year: e.target.value })} placeholder="Année" /></div>
        <div>
          <label className="text-xs">Mois (optionnel)</label>
          <select
            data-testid="achievement-input-month"
            className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background"
            value={f.month || ""}
            onChange={(e) => upd({ month: e.target.value })}
          >
            {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs">Discipline</label>
          <select data-testid="achievement-input-discipline" className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background" value={f.discipline} onChange={(e) => upd({ discipline: e.target.value })}>
            <option value="">—</option>
            {disciplines.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2"><label className="text-xs">Compétition (nom complet)</label><Input data-testid="achievement-input-competition" value={f.competition} onChange={(e) => upd({ competition: e.target.value })} placeholder="Nom complet de la compétition" /></div>
        <div><label className="text-xs">Lieu</label><Input data-testid="achievement-input-place" value={f.place} onChange={(e) => upd({ place: e.target.value })} placeholder="Ville, pays" /></div>
        <div><label className="text-xs">Catégorie</label><Input data-testid="achievement-input-category" value={f.category} onChange={(e) => upd({ category: e.target.value })} placeholder="Ex: Senior H, U18 F" /></div>
        <div>
          <label className="text-xs">Médaille (si applicable)</label>
          <select data-testid="achievement-input-medal" className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background" value={f.medal} onChange={(e) => upd({ medal: e.target.value })}>
            <option value="">—</option>
            <option value="or">Or</option>
            <option value="argent">Argent</option>
            <option value="bronze">Bronze</option>
            <option value="finaliste">Finaliste</option>
            <option value="podium">Podium</option>
          </select>
        </div>
        <div>
          <label className="text-xs">Classement (rang)</label>
          <Input data-testid="achievement-input-rank" type="number" min="1" value={f.rank} onChange={(e) => upd({ rank: e.target.value })} placeholder="Ex: 1, 2, 3…" />
        </div>
        <div>
          <label className="text-xs">Niveau de compétition</label>
          <select data-testid="achievement-input-level" className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background" value={f.level} onChange={(e) => upd({ level: e.target.value })}>
            <option value="">—</option>
            <option value="local">Local</option>
            <option value="territorial">Territorial</option>
            <option value="national">National</option>
            <option value="regional">Régional</option>
            <option value="oceanien">Océanien</option>
            <option value="continental">Continental</option>
            <option value="international">International</option>
            <option value="mondial">Mondial</option>
            <option value="jeux-du-pacifique">Jeux du Pacifique</option>
            <option value="olympique">Olympique</option>
          </select>
        </div>
        <div>
          <label className="text-xs">Type de source</label>
          <select data-testid="achievement-input-source-type" className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background" value={f.result_type} onChange={(e) => upd({ result_type: e.target.value })}>
            <option value="officiel">Officiel (fédération / résultats)</option>
            <option value="presse">Article de presse</option>
            <option value="profil_sportif">Profil sportif en ligne</option>
            <option value="declare">Déclaré par moi-même</option>
            <option value="archive">Archive personnelle</option>
            <option value="a_confirmer">À confirmer</option>
          </select>
        </div>

        {/* --- Champs SPÉCIFIQUES selon la famille --- */}
        {isCombat && (
          <>
            <div className="sm:col-span-2 mt-1 pt-2 border-t border-dashed">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Champs spécifiques — Combat</span>
            </div>
            <div><label className="text-xs">Catégorie de poids</label><Input data-testid="achievement-input-weight" value={f.weight} onChange={(e) => upd({ weight: e.target.value })} placeholder="Ex: -73kg, Super Heavy" /></div>
            <div><label className="text-xs">Classe d'âge</label><Input data-testid="achievement-input-age-class" value={f.age_class} onChange={(e) => upd({ age_class: e.target.value })} placeholder="Ex: Senior, Junior" /></div>
            <div><label className="text-xs">Ceinture / grade</label><Input data-testid="achievement-input-belt" value={f.belt} onChange={(e) => upd({ belt: e.target.value })} placeholder="Ex: noire, bleue" /></div>
            <div><label className="text-xs">Adversaire (finale)</label><Input data-testid="achievement-input-opponent" value={f.opponent} onChange={(e) => upd({ opponent: e.target.value })} placeholder="Nom (optionnel)" /></div>
            <div>
              <label className="text-xs">Résultat final</label>
              <select data-testid="achievement-input-match-outcome" className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background" value={f.match_outcome} onChange={(e) => upd({ match_outcome: e.target.value })}>
                <option value="">—</option>
                <option value="victoire">Victoire</option>
                <option value="defaite">Défaite</option>
                <option value="abandon">Abandon</option>
                <option value="disqualification">Disqualification</option>
              </select>
            </div>
          </>
        )}

        {isTeam && (
          <>
            <div className="sm:col-span-2 mt-1 pt-2 border-t border-dashed">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Champs spécifiques — Sport collectif</span>
            </div>
            <div className="sm:col-span-2"><label className="text-xs">Équipe / Sélection</label><Input data-testid="achievement-input-team-name" value={f.team_name} onChange={(e) => upd({ team_name: e.target.value })} placeholder="Ex: Sélection Tahiti, Club Central" /></div>
          </>
        )}

        {isTimed && (
          <>
            <div className="sm:col-span-2 mt-1 pt-2 border-t border-dashed">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Champs spécifiques — Chronométré</span>
            </div>
            <div><label className="text-xs">Distance / Épreuve</label><Input data-testid="achievement-input-distance" value={f.distance} onChange={(e) => upd({ distance: e.target.value })} placeholder="Ex: 100m, 42.195km" /></div>
            <div><label className="text-xs">Temps</label><Input data-testid="achievement-input-time" value={f.time_performance} onChange={(e) => upd({ time_performance: e.target.value })} placeholder="Ex: 52.34s, 2:51:00" /></div>
          </>
        )}

        {isVaa && (
          <>
            <div className="sm:col-span-2 mt-1 pt-2 border-t border-dashed">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Champs spécifiques — Va'a</span>
            </div>
            <div>
              <label className="text-xs">Format</label>
              <select data-testid="achievement-input-crew-format" className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background" value={f.crew_format} onChange={(e) => upd({ crew_format: e.target.value })}>
                <option value="">—</option>
                <option value="V1">V1 (individuel)</option>
                <option value="V3">V3</option>
                <option value="V6">V6 (équipage)</option>
                <option value="V12">V12</option>
                <option value="V16">V16</option>
              </select>
            </div>
            <div><label className="text-xs">Distance</label><Input data-testid="achievement-input-distance" value={f.distance} onChange={(e) => upd({ distance: e.target.value })} placeholder="Ex: 500m, 22km" /></div>
            <div><label className="text-xs">Équipage / Club</label><Input data-testid="achievement-input-team-name" value={f.team_name} onChange={(e) => upd({ team_name: e.target.value })} placeholder="Nom équipage" /></div>
            <div><label className="text-xs">Temps</label><Input data-testid="achievement-input-time" value={f.time_performance} onChange={(e) => upd({ time_performance: e.target.value })} placeholder="Ex: 1:52.34" /></div>
          </>
        )}

        {isStrength && (
          <>
            <div className="sm:col-span-2 mt-1 pt-2 border-t border-dashed">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Champs spécifiques — Force</span>
            </div>
            <div><label className="text-xs">Catégorie de poids</label><Input data-testid="achievement-input-weight" value={f.weight} onChange={(e) => upd({ weight: e.target.value })} placeholder="Ex: -81kg" /></div>
          </>
        )}

        {isRacket && (
          <>
            <div className="sm:col-span-2 mt-1 pt-2 border-t border-dashed">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Champs spécifiques — Raquette</span>
            </div>
            <div><label className="text-xs">Partenaire (double)</label><Input data-testid="achievement-input-team-name" value={f.team_name} onChange={(e) => upd({ team_name: e.target.value })} placeholder="Nom du partenaire (si double)" /></div>
          </>
        )}

        {/* --- Flags universels --- */}
        <div className="sm:col-span-2 mt-1 pt-2 border-t border-dashed grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="achievement-major-label">
            <Checkbox checked={!!f.is_major_title} onCheckedChange={(v) => upd({ is_major_title: !!v })} data-testid="achievement-input-major" />
            <span className="text-xs">Titre majeur (mise en avant)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="achievement-collective-label">
            <Checkbox checked={!!f.is_collective} onCheckedChange={(v) => upd({ is_collective: !!v })} data-testid="achievement-input-collective" />
            <span className="text-xs">Résultat collectif (équipe / équipage)</span>
          </label>
        </div>

        <div className="sm:col-span-2"><label className="text-xs">Source / lien (optionnel)</label><Input data-testid="achievement-input-source-url" value={f.source_url} onChange={(e) => upd({ source_url: e.target.value })} placeholder="https://… (lien vers la source officielle)" /></div>
        <div className="sm:col-span-2"><label className="text-xs">Photo associée (URL, optionnel)</label><Input data-testid="achievement-input-photo" value={f.photo_url} onChange={(e) => upd({ photo_url: e.target.value })} placeholder="https://… (URL d'une photo)" /></div>
        <div className="sm:col-span-2"><label className="text-xs">Commentaire personnel (optionnel)</label><Textarea data-testid="achievement-input-comment" rows={2} value={f.personal_comment} onChange={(e) => upd({ personal_comment: e.target.value })} placeholder="Commentaire ou contexte (optionnel)" /></div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2 justify-end">
        <Button variant="ghost" onClick={reset} data-testid="achievement-form-reset"><RotateCcw className="h-4 w-4 mr-1" />Réinitialiser</Button>
        <Button variant="outline" onClick={onCancel} data-testid="achievement-form-cancel">Annuler</Button>
        <Button onClick={submit} disabled={saving || !f.title.trim()} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="achievement-form-submit">
          {saving ? "Enregistrement…" : (isEdit ? "Mettre à jour" : "Enregistrer")}
        </Button>
      </div>
    </Card>
  );
}

// V13.7 — Barre d'actions groupées pour le palmarès (édition en masse).
// Permet d'appliquer un même changement à plusieurs résultats cochés en une
// seule opération : "Collectif oui/non", "Type de résultat", "Titre majeur".
function PalmaresBulkActions({ athlete, selected, setSelected, onApply }) {
  const count = selected.size;
  const total = (athlete?.achievements || []).length;
  const allChecked = count === total && total > 0;
  const [busy, setBusy] = useState(false);

  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(Array.from({ length: total }, (_, i) => i)));
  };

  const apply = async (patch, label) => {
    if (count === 0) { toast.info("Coche d'abord au moins un résultat."); return; }
    if (!confirm(`Appliquer « ${label} » à ${count} résultat${count > 1 ? "s" : ""} ?`)) return;
    setBusy(true);
    try { await onApply(patch); } finally { setBusy(false); }
  };

  return (
    <div
      className={`sticky top-0 z-10 rounded-lg border bg-background/95 backdrop-blur-sm p-2.5 flex flex-wrap items-center gap-2 transition-all ${count > 0 ? "border-[hsl(var(--aito-lagoon))] shadow-sm" : "border-dashed"}`}
      data-testid="palmares-bulk-actions"
    >
      <label className="flex items-center gap-2 text-xs cursor-pointer shrink-0">
        <input
          type="checkbox"
          checked={allChecked}
          onChange={toggleAll}
          className="h-4 w-4"
          data-testid="bulk-select-all"
        />
        <span className="font-medium">
          {count === 0 ? "Édition en masse" : `${count}/${total} sélectionné${count > 1 ? "s" : ""}`}
        </span>
      </label>
      <div className="w-px h-5 bg-border shrink-0 hidden sm:block" />
      <div className={`flex flex-wrap items-center gap-1.5 ${count === 0 ? "opacity-50 pointer-events-none" : ""}`}>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Collectif :</span>
        <Button size="sm" variant="outline" onClick={() => apply({ is_collective: true }, "Marquer comme collectif")} disabled={busy} data-testid="bulk-collective-on">Oui</Button>
        <Button size="sm" variant="outline" onClick={() => apply({ is_collective: false }, "Marquer comme individuel")} disabled={busy} data-testid="bulk-collective-off">Non</Button>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground ml-2">Type :</span>
        <Button size="sm" variant="outline" onClick={() => apply({ result_type: "declaratif" }, "Déclaratif")} disabled={busy} data-testid="bulk-type-declaratif">Déclaratif</Button>
        <Button size="sm" variant="outline" onClick={() => apply({ result_type: "officiel_federation" }, "Officiel fédération")} disabled={busy} data-testid="bulk-type-federation">Officiel fédé.</Button>
        <Button size="sm" variant="outline" onClick={() => apply({ result_type: "autre" }, "Autre")} disabled={busy} data-testid="bulk-type-autre">Autre</Button>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground ml-2">Titre majeur :</span>
        <Button size="sm" variant="outline" onClick={() => apply({ is_major_title: true }, "Marquer titre majeur")} disabled={busy} data-testid="bulk-major-on">Oui</Button>
        <Button size="sm" variant="outline" onClick={() => apply({ is_major_title: false }, "Retirer titre majeur")} disabled={busy} data-testid="bulk-major-off">Non</Button>
      </div>
      {count > 0 && (
        <Button size="sm" variant="ghost" className="ml-auto text-xs" onClick={() => setSelected(new Set())} data-testid="bulk-clear">
          <RotateCcw className="h-3 w-3 mr-1" /> Effacer sélection
        </Button>
      )}
    </div>
  );
}

// V13.7 — Bloc UI pour la demande / affichage de la validation officielle de la fiche.
// L'athlète peut demander une validation ; l'affichage est adaptatif selon l'état
// (none / pending / validated / inconsistency) et propose une action de relance.
function ProfileValidationBlock({ athlete, onChanged }) {
  const [busy, setBusy] = useState(false);
  const status = athlete.profile_validation_status || "none";
  const roleTxt = athlete.profile_validation_by_role;
  const fedName = athlete.profile_validation_federation_name;

  const requestValidation = async () => {
    const msg = prompt("Message accompagnant ta demande (facultatif) :", "");
    if (msg === null) return;
    setBusy(true);
    try {
      await api.post(`/athletes/${athlete.id}/validation/request`, { message: msg || null });
      const { data } = await api.get(`/athletes/${athlete.id}`);
      onChanged(data);
      toast.success("Demande envoyée. Le COPF ou ta fédération d'affiliation sera notifié.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'envoyer la demande.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border p-3 bg-muted/30" data-testid="profile-validation-block">
      {status === "none" && (
        <>
          <p className="text-sm mb-2">Ta fiche n'est pas encore soumise à validation officielle.</p>
          <Button size="sm" onClick={requestValidation} disabled={busy} data-testid="request-validation-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}
            Demander la validation officielle
          </Button>
        </>
      )}
      {status === "pending" && (
        <>
          <p className="text-sm">
            <Loader2 className="inline h-4 w-4 mr-1 animate-spin text-amber-600" />
            <strong>Demande en attente</strong> — le COPF ou ta fédération d'affiliation va examiner ta fiche.
          </p>
          {athlete.profile_validation_requested_at && (
            <p className="text-xs text-muted-foreground mt-1">Envoyée le {new Date(athlete.profile_validation_requested_at).toLocaleDateString("fr-FR")}</p>
          )}
        </>
      )}
      {status === "validated" && (
        <>
          <p className="text-sm text-emerald-700 font-medium">
            <ShieldCheck className="inline h-4 w-4 mr-1" />
            Fiche validée {roleTxt === "federation" ? `par ${fedName || "la Fédération"}` : "par le COPF"}
          </p>
          {athlete.profile_validation_at && (
            <p className="text-xs text-muted-foreground mt-1">Validée le {new Date(athlete.profile_validation_at).toLocaleDateString("fr-FR")}</p>
          )}
        </>
      )}
      {status === "inconsistency" && (
        <>
          <p className="text-sm text-orange-700 font-semibold flex items-center gap-1">
            <AlertTriangle className="h-4 w-4" /> Incohérence signalée
          </p>
          {athlete.profile_validation_notes && (
            <blockquote className="text-xs text-orange-700 border-l-2 border-orange-500 pl-2 mt-2">
              « {athlete.profile_validation_notes} »
            </blockquote>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Corrige les points signalés puis relance une demande de validation :
          </p>
          <Button size="sm" onClick={requestValidation} disabled={busy} className="mt-2" data-testid="request-validation-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1.5" />}
            Redemander la validation
          </Button>
        </>
      )}
    </div>
  );
}



const ARTICLE_KIND_OPTIONS = [
  { value: "article", label: "Article" },
  { value: "interview", label: "Interview" },
  { value: "portrait", label: "Portrait" },
  { value: "video", label: "Vidéo" },
  { value: "resultat", label: "Résultat" },
  { value: "publication_institutionnelle", label: "Publication institutionnelle" },
  { value: "reportage_photo", label: "Reportage photo" },
];

const EMPTY_ARTICLE = {
  title: "", url: "", description: "", image_url: "", media: "", date: "",
  discipline: "", kind: "article", source_type: "presse",
};

function ArticleInlineForm({ athleteId, disciplines, initialValue, editIndex, onSaved, onCancel }) {
  const isEdit = editIndex !== undefined && editIndex !== null;
  const buildInitial = () => {
    if (!initialValue) return { ...EMPTY_ARTICLE };
    return {
      ...EMPTY_ARTICLE,
      ...Object.fromEntries(Object.entries(initialValue).map(([k, v]) => [k, v == null ? "" : v])),
      description: initialValue.description || initialValue.summary || "",
      image_url: initialValue.image_url || initialValue.photo_url || "",
    };
  };
  const [f, setF] = useState(buildInitial());
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  useEffect(() => { setF(buildInitial()); /* eslint-disable-next-line */ }, [editIndex, initialValue?.url]);

  const upd = (patch) => setF((c) => ({ ...c, ...patch }));
  const reset = () => setF(isEdit ? buildInitial() : { ...EMPTY_ARTICLE });

  const fetchPreview = async () => {
    const u = f.url.trim();
    if (!u) { toast.error("Saisissez d'abord une URL."); return; }
    setFetching(true);
    try {
      const { data } = await api.post("/ai/url-preview", { url: u });
      const next = { ...f };
      if (data.title && !f.title) next.title = data.title;
      if (data.description && !f.description) next.description = data.description;
      if (data.image_url && !f.image_url) next.image_url = data.image_url;
      if (data.media && !f.media) next.media = data.media;
      if (data.date && !f.date) next.date = data.date;
      if (data.suggested_kind && f.kind === "article") next.kind = data.suggested_kind;
      next.url = data.url;
      setF(next);
      toast.success(data.title ? "Informations récupérées." : "URL analysée — métadonnées limitées, complétez manuellement.");
    } catch (e) { showError(e); }
    finally { setFetching(false); }
  };

  const submit = async () => {
    if (!f.title.trim()) { toast.error("Le titre est obligatoire."); return; }
    if (!f.url.trim()) { toast.error("L'URL est obligatoire."); return; }
    setSaving(true);
    try {
      const payload = {
        title: f.title.trim(),
        url: f.url.trim(),
        description: f.description || null,
        summary: f.description || null,
        image_url: f.image_url || null,
        photo_url: f.image_url || null,
        media: f.media || null,
        date: f.date || null,
        discipline: f.discipline || null,
        kind: f.kind || "article",
        source_type: f.source_type || "presse",
      };
      let data;
      if (isEdit) {
        ({ data } = await api.patch(`/athletes/${athleteId}/list/articles/${editIndex}`, payload));
        toast.success("Article modifié.");
      } else {
        ({ data } = await api.post(`/athletes/${athleteId}/quick-add/article`, payload));
        toast.success("Article ajouté.");
      }
      onSaved(data);
    } catch (e) { showError(e); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-4 border-2 border-[hsl(var(--aito-lagoon))]/40 bg-secondary/30" data-testid="article-inline-form">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-display font-semibold text-sm">{isEdit ? "Modifier cet article" : "Ajouter un article ou un média"}</h4>
        <Button variant="ghost" size="icon" onClick={onCancel} data-testid="article-form-close"><X className="h-4 w-4" /></Button>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-3">
          <label className="text-xs">URL <span className="text-destructive">*</span></label>
          <div className="flex gap-2">
            <Input data-testid="article-input-url" value={f.url} onChange={(e) => upd({ url: e.target.value })} placeholder="https://… ou https://youtube.com/watch?v=…" />
            <Button onClick={fetchPreview} disabled={fetching || !f.url.trim()} className="shrink-0 bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="article-fetch-preview">
              {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              <span className="hidden sm:inline ml-1.5">Récupérer infos auto</span>
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Astuce&nbsp;: collez l'URL d'un article, d'une vidéo YouTube ou d'une publication, puis cliquez «&nbsp;Récupérer infos auto&nbsp;».</p>
        </div>
        <div className="sm:col-span-3">
          <label className="text-xs">Titre <span className="text-destructive">*</span></label>
          <Input data-testid="article-input-title" value={f.title} onChange={(e) => upd({ title: e.target.value })} placeholder="Titre de l'article" />
        </div>
        <div>
          <label className="text-xs">Type</label>
          <select data-testid="article-input-kind" className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background" value={f.kind} onChange={(e) => upd({ kind: e.target.value })}>
            {ARTICLE_KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </div>
        <div><label className="text-xs">Média / source</label><Input data-testid="article-input-media" value={f.media} onChange={(e) => upd({ media: e.target.value })} placeholder="Tahiti Infos, La 1ère, …" /></div>
        <div><label className="text-xs">Date</label><Input data-testid="article-input-date" value={f.date} onChange={(e) => upd({ date: e.target.value })} placeholder="2024-08-15 ou « Août 2024 »" /></div>
        <div className="sm:col-span-2">
          <label className="text-xs">Discipline</label>
          <select data-testid="article-input-discipline" className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background" value={f.discipline} onChange={(e) => upd({ discipline: e.target.value })}>
            <option value="">—</option>
            {disciplines.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs">Type de source</label>
          <select data-testid="article-input-source-type" className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background" value={f.source_type} onChange={(e) => upd({ source_type: e.target.value })}>
            <option value="officiel">Officiel</option>
            <option value="presse">Article de presse</option>
            <option value="profil_sportif">Profil sportif</option>
            <option value="publication_institutionnelle">Publication institutionnelle</option>
            <option value="video">Vidéo</option>
            <option value="declare">Déclaré par l'athlète</option>
            <option value="archive">Archive personnelle</option>
            <option value="autre">Autre</option>
          </select>
        </div>
        <div className="sm:col-span-3">
          <label className="text-xs">Miniature (URL image)</label>
          <div className="flex items-start gap-2 mt-1">
            {f.image_url ? (
              <img src={f.image_url} alt="" className="h-20 w-32 object-cover rounded border shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
              <div className="h-20 w-32 rounded border bg-muted flex items-center justify-center shrink-0"><ImageIcon className="h-6 w-6 text-muted-foreground" /></div>
            )}
            <Input data-testid="article-input-image" value={f.image_url} onChange={(e) => upd({ image_url: e.target.value })} placeholder="https://… (auto-détectée si vide)" />
          </div>
        </div>
        <div className="sm:col-span-3"><label className="text-xs">Résumé court</label><Textarea data-testid="article-input-description" rows={3} value={f.description} onChange={(e) => upd({ description: e.target.value })} placeholder="2 à 3 phrases résumant l'article…" /></div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2 justify-end">
        <Button variant="ghost" onClick={reset} data-testid="article-form-reset"><RotateCcw className="h-4 w-4 mr-1" />Réinitialiser</Button>
        <Button variant="outline" onClick={onCancel} data-testid="article-form-cancel">Annuler</Button>
        <Button onClick={submit} disabled={saving || !f.title.trim() || !f.url.trim()} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="article-form-submit">
          {saving ? "Enregistrement…" : (isEdit ? "Mettre à jour" : "Enregistrer")}
        </Button>
      </div>
    </Card>
  );
}

function CustomDisciplineButton({ athlete, onChange }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const submit = async () => {
    const v = val.trim();
    if (!v) { toast.error("Indiquez le nom de la discipline."); return; }
    try {
      const { data } = await api.post(`/athletes/${athlete.id}/quick-add/discipline`, { value: v });
      onChange(data);
      setVal(""); setOpen(false);
      toast.success(`Discipline « ${v} » ajoutée.`);
    } catch (e) { showError(e); }
  };
  if (!open) {
    return (
      <button type="button" data-testid="edit-discipline-other" onClick={() => setOpen(true)}
        className="px-3 py-1.5 rounded-full text-xs border border-dashed hover:bg-muted">
        + Autre…
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 border rounded-full pl-3 pr-1 py-1 text-xs bg-secondary/50" data-testid="edit-discipline-other-form">
      <Input data-testid="edit-discipline-other-input" autoFocus value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Préciser la discipline" className="h-7 text-xs border-0 bg-transparent focus-visible:ring-0 px-1 min-w-[180px]" />
      <Button size="sm" className="h-7 px-2 bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" onClick={submit} data-testid="edit-discipline-other-submit">Ajouter</Button>
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setOpen(false); setVal(""); }} data-testid="edit-discipline-other-cancel"><X className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

// V13.18 — Éditeur simple de liste de tags (disciplines_taught, qualifications, …)
function TagListEditor({ values = [], onChange, placeholder, hint, "data-testid": testid }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v || values.includes(v)) { setInput(""); return; }
    onChange([...values, v]);
    setInput("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          data-testid={testid}
        />
        <Button type="button" variant="outline" onClick={add} disabled={!input.trim()}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {values.map((v) => (
            <Badge key={v} variant="secondary" className="pl-2 pr-1 py-0.5 flex items-center gap-1">
              <span className="text-[11px]">{v}</span>
              <button
                type="button"
                onClick={() => onChange(values.filter((x) => x !== v))}
                className="hover:text-destructive focus-ring rounded"
                aria-label={`Retirer ${v}`}
                data-testid={`${testid}-remove-${v}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      {hint && <p className="text-[10px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function SectionCard({ title, description, children, action }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-display font-semibold">{title}</h3>
          {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        </div>        {action}
      </div>
      {children}
    </Card>
  );
}

function StringListEditor({ items, kind, athleteId, onChange, label, placeholder }) {
  const [val, setVal] = useState("");
  const add = async () => {
    if (!val.trim()) return;
    const { data } = await api.post(`/athletes/${athleteId}/quick-add/${kind}`, { value: val.trim() });
    onChange(data);
    setVal("");
  };
  const remove = async (i) => {
    const field = kind === "monitored_link" ? "monitored_links" : kind === "top_title" ? "top_titles" : `${kind}s`;
    const { data } = await api.delete(`/athletes/${athleteId}/list/${field}/${i}`);
    onChange(data);
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {(items || []).map((v, i) => (
          <Badge key={i} variant="secondary" className="pl-2.5 pr-1 gap-1">
            {v}
            <button onClick={() => remove(i)} data-testid={`remove-${kind}-${i}`} className="ml-1 hover:text-destructive"><Trash2 className="h-3 w-3" /></button>
          </Badge>
        ))}
        {(items || []).length === 0 && <span className="text-xs text-muted-foreground">Rien à afficher pour l'instant. Cliquez sur « Ajouter » pour commencer.</span>}
      </div>
      <div className="flex gap-2">
        <Input data-testid={`input-${kind}`} value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button size="sm" onClick={add} data-testid={`add-${kind}`}><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

// V12.x — Éditeur générique de liens externes (label + url)
function ExternalProfilesEditor({ items, onChange }) {
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const list = Array.isArray(items) ? items : [];

  const add = () => {
    const l = label.trim();
    const u = url.trim();
    if (!u) { toast.error("L'URL est obligatoire."); return; }
    if (!/^https?:\/\//i.test(u)) { toast.error("URL invalide (http/https requis)."); return; }
    if (list.some((it) => it.url === u)) { toast.info("Ce lien est déjà ajouté."); return; }
    const next = [...list, { label: l || "Voir le profil", url: u }];
    onChange(next);
    setLabel(""); setUrl("");
  };
  const remove = (i) => {
    const next = list.filter((_, idx) => idx !== i);
    onChange(next);
  };
  const updateAt = (i, field, value) => {
    const next = list.map((it, idx) => idx === i ? { ...it, [field]: value } : it);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {list.length > 0 && (
        <ul className="space-y-2" data-testid="external-profiles-list">
          {list.map((it, i) => (
            <li key={i} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center bg-secondary/30 border rounded-md p-2">
              <Input
                className="sm:w-56"
                value={it.label || ""}
                onChange={(e) => updateAt(i, "label", e.target.value)}
                onBlur={(e) => onChange(list.map((x, idx) => idx === i ? { ...x, label: e.target.value.trim() || "Voir le profil" } : x))}
                placeholder="Libellé (ex: Profil ICF)"
                data-testid={`external-profile-label-${i}`}
              />
              <Input
                className="flex-1 font-mono text-xs"
                type="url"
                value={it.url || ""}
                onChange={(e) => updateAt(i, "url", e.target.value)}
                onBlur={(e) => onChange(list.map((x, idx) => idx === i ? { ...x, url: e.target.value.trim() } : x))}
                placeholder="https://..."
                data-testid={`external-profile-url-${i}`}
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => remove(i)}
                data-testid={`external-profile-remove-${i}`}
                title="Retirer ce lien"
              >
                <Trash2 className="h-4 w-4 text-red-700" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr_auto] gap-2">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Libellé (ex: Wikipedia, Fédération ICF…)"
          data-testid="external-profile-new-label"
        />
        <Input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="https://exemple.com/mon-profil"
          data-testid="external-profile-new-url"
        />
        <Button
          onClick={add}
          size="sm"
          data-testid="external-profile-add"
        >
          <Plus className="h-4 w-4 mr-1" /> Ajouter
        </Button>
      </div>
      {list.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Aucun lien externe pour l'instant. Ajoutez par exemple votre profil sur la fédération internationale, votre page Wikipedia, votre chaîne YouTube, ou un article de presse de référence.
        </p>
      )}
    </div>
  );
}


export default function AthleteEditPage() {
  const { user, loading: authLoading } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disciplines, setDisciplines] = useState([]);

  useEffect(() => { if (!authLoading && !user) nav("/connexion"); }, [user, authLoading, nav]);
  useEffect(() => {
    api.get(`/athletes/${id}`).then((r) => setA(r.data)).catch(() => setA(null)).finally(() => setLoading(false));
    api.get("/disciplines").then((r) => setDisciplines(r.data.items || []));
  }, [id]);

  const patch = async (body) => {
    setSaving(true);
    try { const { data } = await api.patch(`/athletes/${a.id}`, body); setA(data); toast.success("Modifications enregistrées."); }
    catch (e) { showError(e); }
    finally { setSaving(false); }
  };

  const [showAchForm, setShowAchForm] = useState(false);
  const [editAchIndex, setEditAchIndex] = useState(null);
  // V13.7 — Sélection multiple pour édition en masse du palmarès
  const [bulkSel, setBulkSel] = useState(new Set());
  const [showArtForm, setShowArtForm] = useState(false);
  const [editArtIndex, setEditArtIndex] = useState(null);

  const openAchEdit = (i) => { setEditAchIndex(i); setShowAchForm(true); setTimeout(() => document.querySelector('[data-testid="achievement-inline-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };
  const closeAchForm = () => { setShowAchForm(false); setEditAchIndex(null); };
  const toggleAchHidden = async (i, current) => {
    try {
      const { data } = await api.patch(`/athletes/${a.id}/list/achievements/${i}`, { hidden: !current });
      setA(data);
      toast.success(current ? "Élément réaffiché." : "Élément masqué de la fiche publique.");
    } catch (e) { showError(e); }
  };
  const removeAch = async (i) => {
    if (!confirm("Supprimer définitivement cet élément du palmarès ?")) return;
    try { const { data } = await api.delete(`/athletes/${a.id}/list/achievements/${i}`); setA(data); toast.success("Élément supprimé."); }
    catch (e) { showError(e); }
  };

  const openArtEdit = (i) => { setEditArtIndex(i); setShowArtForm(true); setTimeout(() => document.querySelector('[data-testid="article-inline-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };
  const closeArtForm = () => { setShowArtForm(false); setEditArtIndex(null); };
  const toggleArtHidden = async (i, current) => {
    try {
      const { data } = await api.patch(`/athletes/${a.id}/list/articles/${i}`, { hidden: !current });
      setA(data);
      toast.success(current ? "Article réaffiché." : "Article masqué de la fiche publique.");
    } catch (e) { showError(e); }
  };
  const removeArt = async (i) => {
    if (!confirm("Supprimer définitivement cet article ?")) return;
    try { const { data } = await api.delete(`/athletes/${a.id}/list/articles/${i}`); setA(data); toast.success("Article supprimé."); }
    catch (e) { showError(e); }
  };

  // Quick-add helpers
  const addResult = async () => {
    const competition = prompt("Compétition ?");
    if (!competition) return;
    const result = prompt("Résultat (1er, 2e, finaliste, …) ?") || "";
    const date = prompt("Date (YYYY-MM-DD) ?") || null;
    const { data } = await api.post(`/athletes/${a.id}/quick-add/result`, { competition, result, date });
    setA(data); toast.success("Résultat ajouté.");
  };
  const addPhoto = async () => {
    const url = prompt("URL de la photo ?");
    if (!url) return;
    if (!confirm("Je confirme disposer des droits nécessaires pour publier cette photo sur AITO.PF.")) return;
    const caption = prompt("Légende (optionnel) ?") || null;
    const { data } = await api.post(`/athletes/${a.id}/quick-add/photo`, { url, caption, rights_confirmed: true });
    setA(data); toast.success("Photo ajoutée.");
  };
  const addVideo = async () => {
    const url = prompt("URL de la vidéo (YouTube/Facebook/…) ?");
    if (!url) return;
    const title = prompt("Titre (optionnel) ?") || null;
    const { data } = await api.post(`/athletes/${a.id}/quick-add/video`, { url, title });
    setA(data); toast.success("Vidéo ajoutée.");
  };

  const downloadPdf = async () => {
    try {
      const res = await api.post(`/ai/sponsor-pdf/athlete/${a.id}`, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a"); link.href = url; link.download = `AITO_PF_Fiche_Sportive_${a.slug}.pdf`; link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Fiche sportive PDF régénérée.");
    } catch (e) { showError(e, { prefix: "Erreur PDF: " }); }
  };

  if (loading) return <div className="p-8 mx-auto max-w-4xl"><Skeleton className="h-12 w-1/2" /><Skeleton className="mt-4 h-64" /></div>;
  if (!a) return <div className="p-8">Fiche introuvable. <Link to="/dashboard" className="text-[hsl(var(--aito-lagoon))]">Retour</Link></div>;

  /* ------------------ Sponsor Partners Editor (inline) ------------------ */
  function SponsorPartnersEditor({ items = [], onChange, athleteId }) {
    const [draft, setDraft] = useState({ name: "", logo_url: "", url: "", tier: "standard", since_year: "" });
    const [saving, setSaving] = useState(false);

    // V9.11 — Toute modification de la liste persiste immédiatement via PATCH.
    //   Sans ce PATCH, l'ajout ne restait qu'en state React local et disparaissait
    //   au rechargement de la page. `onChange` reçoit l'athlète complet renvoyé
    //   par le serveur pour synchroniser tout le state.
    const persist = async (nextItems) => {
      setSaving(true);
      try {
        const { data } = await api.patch(`/athletes/${athleteId}`, { sponsor_partners: nextItems });
        onChange(data);
        toast.success("Partenaires enregistrés.");
      } catch (e) {
        showError(e, { fallback: "Échec de l'enregistrement des partenaires." });
      } finally {
        setSaving(false);
      }
    };

    const add = async () => {
      if (!draft.name.trim()) return;
      const newItem = {
        name: draft.name.trim(),
        logo_url: draft.logo_url.trim() || null,
        url: draft.url.trim() || null,
        tier: draft.tier || "standard",
        since_year: draft.since_year ? parseInt(draft.since_year, 10) : null,
      };
      await persist([...items, newItem]);
      setDraft({ name: "", logo_url: "", url: "", tier: "standard", since_year: "" });
    };
    const remove = async (idx) => {
      await persist(items.filter((_, i) => i !== idx));
    };
    const tierTone = (t) => t === "gold" ? "border-[hsl(var(--aito-gold))]" : t === "silver" ? "border-slate-400" : t === "bronze" ? "border-amber-700" : "border-border";
    return (
      <div className="space-y-3" data-testid="sponsor-partners-editor">
        {items.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Aucun partenaire officiel. Ajoute ton premier partenaire ci-dessous (logo + lien).</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {items.map((p, idx) => (
              <div key={idx} className={`relative aspect-square rounded-lg border-2 ${tierTone(p.tier)} bg-background flex flex-col items-center justify-center p-2 group`} data-testid={`partner-editor-${idx}`}>
                {p.logo_url ? (
                  <img src={p.logo_url} alt={p.name} className="max-h-12 max-w-full object-contain" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center"><span className="text-[10px] text-muted-foreground">No logo</span></div>
                )}
                <div className="text-[10px] font-medium text-center mt-1.5 leading-tight line-clamp-2 break-words">{p.name}</div>
                {p.tier && p.tier !== "standard" && (
                  <span className="absolute top-1 right-1 text-[8px] px-1 py-0 rounded bg-muted border capitalize">{p.tier}</span>
                )}
                <button type="button" onClick={() => remove(idx)} data-testid={`partner-remove-${idx}`} className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center" aria-label="Retirer">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="border-dashed border-2 border-border/60 rounded-lg p-3 space-y-2 bg-muted/20">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Ajouter un partenaire</div>
          <p className="text-[11px] text-muted-foreground -mt-1">
            Astuce : pour le logo, colle <em>l'URL publique</em> du fichier image (clic droit sur le logo → « Copier l'adresse de l'image »).
            L'enregistrement est automatique dès que tu cliques sur le bouton ci-dessous.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input className="border rounded px-2 py-1.5 text-sm bg-background" placeholder="Nom du partenaire" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="partner-form-name" />
            <input className="border rounded px-2 py-1.5 text-sm bg-background" placeholder="URL du logo (https://...)" value={draft.logo_url} onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })} data-testid="partner-form-logo" />
            <input className="border rounded px-2 py-1.5 text-sm bg-background" placeholder="Site web (https://...)" value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} data-testid="partner-form-url" />
            <div className="flex items-center gap-2">
              <select className="flex-1 border rounded px-2 py-1.5 text-sm bg-background" value={draft.tier} onChange={(e) => setDraft({ ...draft, tier: e.target.value })} data-testid="partner-form-tier">
                <option value="gold">Or — partenaire principal</option>
                <option value="silver">Argent</option>
                <option value="bronze">Bronze</option>
                <option value="standard">Standard</option>
              </select>
              <input type="number" className="w-24 border rounded px-2 py-1.5 text-sm bg-background" placeholder="Année" value={draft.since_year} onChange={(e) => setDraft({ ...draft, since_year: e.target.value })} data-testid="partner-form-year" />
            </div>
          </div>
          <Button size="sm" onClick={add} disabled={!draft.name.trim() || saving} data-testid="partner-form-add">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
            {saving ? "Enregistrement…" : "Ajouter le partenaire"}
          </Button>
        </div>
      </div>
    );
  }
  /* --------------------------------------------------------------------- */

  return (
    <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/dashboard"><Button variant="ghost" size="sm" data-testid="edit-back"><ArrowLeft className="h-4 w-4 mr-1" />Mon espace</Button></Link>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold">Modifier ma fiche</h1>
            <p className="text-xs text-muted-foreground">{a.full_name}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/athletes/${a.slug}`}><Button variant="outline" size="sm" data-testid="edit-view-public">Voir ma fiche</Button></Link>
          <Button size="sm" onClick={downloadPdf} className="bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(43_74%_45%)]" data-testid="edit-regenerate-pdf"><FileDown className="h-4 w-4 mr-1.5" />Exporter ma fiche sportive en PDF</Button>
        </div>
      </div>

      <Tabs defaultValue="assistant">
        <TabsList className="w-full justify-start overflow-x-auto no-scrollbar">
          <TabsTrigger value="assistant" data-testid="edit-tab-assistant" className="text-[hsl(var(--aito-lagoon))]">
            ✨ Assistant IA
          </TabsTrigger>
          <TabsTrigger value="identity" data-testid="edit-tab-identity">Identité</TabsTrigger>
          <TabsTrigger value="sport" data-testid="edit-tab-sport">Sport</TabsTrigger>
          <TabsTrigger value="palmares" data-testid="edit-tab-palmares">Palmarès & Résultats</TabsTrigger>
          <TabsTrigger value="network" data-testid="edit-tab-network">Coachs, clubs, sponsors</TabsTrigger>
          <TabsTrigger value="media" data-testid="edit-tab-media">Photos / Vidéos / Articles</TabsTrigger>
          <TabsTrigger value="monitor" data-testid="edit-tab-monitor">Veille IA</TabsTrigger>
          <TabsTrigger value="history" data-testid="edit-tab-history">Historique</TabsTrigger>
        </TabsList>

        <TabsContent value="assistant" className="mt-4">
          <AIAssistantPanel
            athleteId={a.id}
            athlete={a}
            onApplied={async () => {
              try {
                const { data } = await api.get(`/athletes/${a.id}`);
                setA(data);
              } catch { /* ignore */ }
            }}
          />
        </TabsContent>

        <TabsContent value="identity" className="mt-4 space-y-4">
          <SectionCard title="Type de profil & niveau sportif" description="Type de profil = ton rôle (actif, amateur, coach…). Niveau sportif = ta reconnaissance (loisir, compétiteur, haut niveau…). Ces deux champs sont indépendants.">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs">Type de profil</label>
                <select data-testid="edit-profile-type" className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background" value={a.profile_type || "non_specifie"} onChange={(e) => patch({ profile_type: e.target.value })}>
                  <option value="non_specifie">Non spécifié</option>
                  <option value="actif">Athlète actif</option>
                  <option value="athlete_professionnel">Athlète professionnel</option>
                  <option value="athlete_amateur">Athlète amateur / loisir</option>
                  <option value="ancien_athlete">Ancien athlète</option>
                  <option value="coach">Coach</option>
                  <option value="dirigeant_sportif">Dirigeant sportif</option>
                  <option value="benevole_sportif">Bénévole sportif</option>
                  <option value="parcours_a_completer">Parcours sportif à compléter</option>
                  {/* Options héritées (conservées pour compat fiches existantes — s'affichent avec les mêmes libellés que les canoniques modernes) */}
                  <option value="en_pause">Athlète en pause</option>
                  <option value="coach_ancien_competiteur">Coach (hérité)</option>
                  <option value="dirigeant_ancien_athlete">Dirigeant sportif (hérité)</option>
                  <option value="benevole_ancien_competiteur">Bénévole sportif (hérité)</option>
                  <option value="competiteur_local">Compétiteur local (hérité)</option>
                  <option value="competiteur_territorial">Compétiteur territorial (hérité)</option>
                  <option value="competiteur_national">Compétiteur national (hérité)</option>
                  <option value="competiteur_regional">Compétiteur régional (hérité)</option>
                  <option value="competiteur_international">Compétiteur international (hérité)</option>
                  <option value="sportif_amateur_confirme">Sportif amateur confirmé (hérité)</option>
                  <option value="sportif_amateur_parcours">Sportif amateur — parcours documenté (hérité)</option>
                  <option value="reconverti">Athlète reconverti (hérité)</option>
                </select>
              </div>
              <div><label className="text-xs">Niveau sportif</label>
                <select data-testid="edit-sport-level" className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background" value={a.sport_level || ""} onChange={(e) => patch({ sport_level: e.target.value || null })}>
                  <option value="">Non spécifié</option>
                  <option value="loisir">Loisir</option>
                  <option value="local">Compétiteur local</option>
                  <option value="territorial">Compétiteur territorial</option>
                  <option value="national">Compétiteur national</option>
                  <option value="regional">Compétiteur régional</option>
                  <option value="international">Compétiteur international</option>
                  <option value="mondial">Niveau mondial</option>
                  <option value="haut_niveau_confirme">Haut niveau confirmé</option>
                  <option value="potentiel_haut_niveau">Potentiel haut niveau</option>
                  <option value="a_completer">À compléter</option>
                </select>
              </div>
              <div><label className="text-xs">Année de début</label><Input data-testid="edit-period-start" type="number" defaultValue={a.period_start || ""} onBlur={(e) => { const v = e.target.value ? parseInt(e.target.value) : null; if (v !== a.period_start) patch({ period_start: v }); }} placeholder="2005" /></div>
              <div><label className="text-xs">Année de fin (vide si actif)</label><Input data-testid="edit-period-end" type="number" defaultValue={a.period_end || ""} onBlur={(e) => { const v = e.target.value ? parseInt(e.target.value) : null; if (v !== a.period_end) patch({ period_end: v }); }} placeholder="présent" /></div>
            </div>
          </SectionCard>

          {/* V13.18 — Volet Coach (rôle secondaire) */}
          <SectionCard
            title={<><UserCheck className="inline h-4 w-4 mr-1 text-[hsl(var(--aito-lagoon))]" />Rôle Coach (secondaire)</>}
            description="Active cette option si tu encadres aussi des athlètes. Une seule identité, deux rôles : tu apparaîtras dans l'annuaire Coach et un onglet Coach dédié s'ajoutera à ta fiche publique."
          >
            <div className="flex items-center justify-between gap-3 pb-3 border-b">
              <div>
                <div className="text-sm font-medium">Je suis aussi coach</div>
                <div className="text-[11px] text-muted-foreground">
                  Ceci active le volet Coach. Aucun doublon n'est créé — ta fiche athlète devient aussi ta fiche coach.
                </div>
              </div>
              <Switch
                data-testid="edit-input-also-offers-coaching"
                checked={!!a.also_offers_coaching}
                onCheckedChange={(v) => patch({ also_offers_coaching: v })}
              />
            </div>

            {a.also_offers_coaching && (
              <div className="space-y-3 mt-3 animate-in fade-in duration-200">
                <div>
                  <label className="text-xs">Bio Coach (optionnelle)</label>
                  <Textarea
                    rows={3}
                    data-testid="edit-input-coach-bio"
                    defaultValue={a.coach_bio || ""}
                    onBlur={(e) => e.target.value !== (a.coach_bio || "") && patch({ coach_bio: e.target.value || null })}
                    placeholder="Décris ton approche pédagogique, ton style d'encadrement, ce qui te motive à transmettre…"
                    maxLength={2000}
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Si vide, la bio athlète sera reprise sur le volet Coach.
                  </p>
                </div>

                <div>
                  <label className="text-xs">Disciplines enseignées</label>
                  <TagListEditor
                    data-testid="edit-input-coach-disciplines"
                    values={a.coach_disciplines_taught || []}
                    onChange={(v) => patch({ coach_disciplines_taught: v })}
                    placeholder="Ex: jiu-jitsu-bresilien, judo, boxe-anglaise…"
                    hint="Si vide, tes disciplines d'athlète seront affichées par défaut."
                  />
                </div>

                <div>
                  <label className="text-xs">Qualifications / diplômes</label>
                  <TagListEditor
                    data-testid="edit-input-coach-qualifications"
                    values={a.coach_qualifications || []}
                    onChange={(v) => patch({ coach_qualifications: v })}
                    placeholder="Ex: Brevet Fédéral, Ceinture noire 2ème dan, DEJEPS…"
                    hint="Ajoute une qualification à la fois, valide avec Entrée."
                  />
                </div>

                <div className="max-w-xs">
                  <label className="text-xs">Année de début coaching</label>
                  <Input
                    type="number"
                    min="1960"
                    max={new Date().getFullYear()}
                    data-testid="edit-input-coach-year"
                    defaultValue={a.coach_year_started || ""}
                    onBlur={(e) => {
                      const v = e.target.value ? parseInt(e.target.value, 10) : null;
                      if (v !== a.coach_year_started) patch({ coach_year_started: v });
                    }}
                    placeholder="Ex: 2015"
                  />
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Jeux du Pacifique 2027"
            description="Sélection officielle : uniquement AITO / COPF peuvent valider. Ta déclaration passe en attente (PENDING) puis devient publique une fois validée."
          >
            <GamesSelectionBlock athleteId={a.id} mainDiscipline={a.main_discipline} />
          </SectionCard>

          <SectionCard title="Identité sportive" description="Nom, surnom, commune, île, photo.">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs">Nom complet</label><Input data-testid="edit-input-name" defaultValue={a.full_name} onBlur={(e) => e.target.value !== a.full_name && patch({ full_name: e.target.value })} /></div>
              <div><label className="text-xs">Surnom</label><Input data-testid="edit-input-nickname" defaultValue={a.nickname || ""} onBlur={(e) => e.target.value !== (a.nickname || "") && patch({ nickname: e.target.value || null })} /></div>
              <div><label className="text-xs">Commune</label><Input data-testid="edit-input-commune" defaultValue={a.commune || ""} onBlur={(e) => e.target.value !== (a.commune || "") && patch({ commune: e.target.value || null })} /></div>
              <div><label className="text-xs">Île</label><Input data-testid="edit-input-island" defaultValue={a.island || ""} onBlur={(e) => e.target.value !== (a.island || "") && patch({ island: e.target.value || null })} /></div>
              {/* V11.19 — Catégorie (utilisée pour la silhouette par défaut si aucune photo) */}
              <div className="sm:col-span-2">
                <label className="text-xs">Catégorie sportive</label>
                <div className="mt-1 flex flex-wrap gap-2" data-testid="edit-input-sex-group">
                  {[
                    { v: "homme", label: "Homme" },
                    { v: "femme", label: "Femme" },
                    { v: "autre", label: "Autre / non précisé" },
                  ].map((opt) => {
                    const selected = (a.sex || "") === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => (a.sex || null) !== opt.v && patch({ sex: opt.v })}
                        data-testid={`edit-input-sex-${opt.v}`}
                        className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                          selected
                            ? "bg-[hsl(var(--aito-lagoon))] text-white border-[hsl(var(--aito-lagoon))]"
                            : "bg-background text-foreground border-border hover:bg-muted"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Utilisée uniquement pour afficher la bonne silhouette par défaut si aucune photo n'est renseignée. Modifiable à tout moment.
                </p>
              </div>
              <div className="sm:col-span-2">
                <AthleteMediaManager
                  athlete={a}
                  onChange={(updated) => setA(updated)}
                />
              </div>
              <details className="sm:col-span-2 rounded-md border border-dashed p-3 text-xs">
                <summary className="cursor-pointer font-medium text-muted-foreground">
                  … ou utiliser une URL externe (option secondaire, remplacée si un upload est présent)
                </summary>
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="text-xs">Photo de profil — URL publique</label>
                    <Input data-testid="edit-input-photo" defaultValue={a.photo_url || ""} placeholder="https://exemple.com/ma-photo.jpg" onBlur={(e) => e.target.value !== (a.photo_url || "") && patch({ photo_url: e.target.value || null })} />
                  </div>
                  <div>
                    <label className="text-xs">Bannière — URL publique</label>
                    <Input data-testid="edit-input-banner" defaultValue={a.banner_url || ""} placeholder="https://exemple.com/ma-banniere.jpg" onBlur={(e) => e.target.value !== (a.banner_url || "") && patch({ banner_url: e.target.value || null })} />
                  </div>
                </div>
              </details>
            </div>
          </SectionCard>

          <SectionCard title="Bio" description="Une bio courte et une bio longue, en français.">
            <div className="space-y-3">
              <div><label className="text-xs">Bio courte (~280 caractères)</label><Textarea data-testid="edit-input-bio-short" rows={2} defaultValue={a.bio_short || ""} onBlur={(e) => e.target.value !== (a.bio_short || "") && patch({ bio_short: e.target.value || null })} /></div>
              {/* V13.6 — Bio longue retirée : le contenu existant a été consolidé dans "Parcours sportif". */}
            </div>
          </SectionCard>

          {/* V11.15 — Réseaux sociaux publics (Facebook / Instagram / TikTok) */}
          <SectionCard title="Réseaux sociaux" description="Ajoute les liens vers tes profils publics. Ils seront affichés en boutons cliquables sur ta fiche.">
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs">Facebook</label>
                <Input
                  data-testid="edit-input-social-facebook"
                  type="url"
                  placeholder="https://facebook.com/tonprofil"
                  defaultValue={a.social_facebook || ""}
                  onBlur={(e) => e.target.value !== (a.social_facebook || "") && patch({ social_facebook: e.target.value.trim() || null })}
                />
              </div>
              <div>
                <label className="text-xs">Instagram</label>
                <Input
                  data-testid="edit-input-social-instagram"
                  type="url"
                  placeholder="https://instagram.com/tonpseudo"
                  defaultValue={a.social_instagram || ""}
                  onBlur={(e) => e.target.value !== (a.social_instagram || "") && patch({ social_instagram: e.target.value.trim() || null })}
                />
              </div>
              <div>
                <label className="text-xs">TikTok</label>
                <Input
                  data-testid="edit-input-social-tiktok"
                  type="url"
                  placeholder="https://tiktok.com/@tonpseudo"
                  defaultValue={a.social_tiktok || ""}
                  onBlur={(e) => e.target.value !== (a.social_tiktok || "") && patch({ social_tiktok: e.target.value.trim() || null })}
                />
              </div>
            </div>

            {/* V11.19 — URLs de posts spécifiques pour aperçu embarqué en sidebar */}
            <div className="mt-4 pt-4 border-t space-y-3">
              <div>
                <div className="text-xs font-semibold text-foreground">Post en vedette (aperçu embarqué)</div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Colle l'URL d'un post <strong>Instagram</strong> (<code>/p/</code>, <code>/reel/</code>) ou <strong>TikTok</strong> (<code>/video/</code>). Il apparaîtra directement dans la sidebar de ta fiche.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs">URL post Instagram</label>
                  <Input
                    data-testid="edit-input-latest-instagram-post"
                    type="url"
                    placeholder="https://www.instagram.com/p/XXXXXXXXX/"
                    defaultValue={a.latest_instagram_post_url || ""}
                    onBlur={(e) => e.target.value !== (a.latest_instagram_post_url || "") && patch({ latest_instagram_post_url: e.target.value.trim() || null })}
                  />
                </div>
                <div>
                  <label className="text-xs">URL vidéo TikTok</label>
                  <Input
                    data-testid="edit-input-latest-tiktok-post"
                    type="url"
                    placeholder="https://www.tiktok.com/@pseudo/video/1234567890"
                    defaultValue={a.latest_tiktok_post_url || ""}
                    onBlur={(e) => e.target.value !== (a.latest_tiktok_post_url || "") && patch({ latest_tiktok_post_url: e.target.value.trim() || null })}
                  />
                </div>
              </div>
            </div>
          </SectionCard>

          {/* V12.x — Liens externes libres (fédération internationale, Wikipedia, site officiel, LinkedIn, chaîne YouTube, etc.) */}
          <SectionCard
            title="Autres liens & profils"
            description="Ajoute tout autre lien utile : profil fédération internationale (ICF, FINA, World Athletics…), page Wikipedia, site officiel, chaîne YouTube, LinkedIn, article de presse de référence… Ces liens seront affichés en boutons cliquables sur ta fiche."
          >
            <ExternalProfilesEditor
              items={Array.isArray(a.external_profiles) ? a.external_profiles : []}
              onChange={(items) => patch({ external_profiles: items })}
            />
          </SectionCard>

          <SectionCard title="Parcours sportif" description="Votre récit (débuts, clubs, entraîneurs, moments marquants…). Particulièrement utile pour les anciens athlètes.">
            <Textarea data-testid="edit-input-journey" rows={8} defaultValue={a.journey_text || ""} onBlur={(e) => e.target.value !== (a.journey_text || "") && patch({ journey_text: e.target.value || null })} placeholder="Raconte ton parcours : débuts, clubs, coachs, compétitions marquantes, évolution, transmission…" />
            <p className="text-[11px] text-muted-foreground mt-2">
              Astuce : les titres majeurs se marquent désormais directement dans <strong>Palmarès & résultats</strong> via la case « Titre majeur » sur chaque résultat.
            </p>
          </SectionCard>

          {/* V13.6 — Statut Athlète de Haut Niveau (SHN) — case auto-déclarative,
              validation admin/COPF/fédération d'affiliation nécessaire pour affichage public. */}
          <SectionCard
            title="Statut d'athlète de haut niveau"
            description="Coche cette case si tu penses relever du statut d'athlète de haut niveau. Ta demande sera vérifiée par un administrateur, le COPF ou ta fédération d'affiliation avant d'apparaître publiquement sur ta fiche."
          >
            <div className="flex items-start gap-3 rounded-md border p-3 bg-muted/30">
              <Checkbox
                id="edit-input-high-level"
                data-testid="edit-input-high-level"
                checked={!!a.is_high_level_claimed}
                onCheckedChange={(v) => patch({ is_high_level_claimed: !!v })}
              />
              <div className="flex-1">
                <label htmlFor="edit-input-high-level" className="text-sm font-medium cursor-pointer">
                  Je déclare avoir le statut d'athlète de haut niveau
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  {a.is_high_level_validated ? (
                    <span className="inline-flex items-center gap-1 text-[hsl(var(--aito-gold))] font-medium">
                      ✓ Statut validé
                      {a.high_level_validated_by_role ? ` par ${a.high_level_validated_by_role.toUpperCase()}` : ""}
                    </span>
                  ) : a.is_high_level_claimed ? (
                    <span className="italic">En attente de validation par un administrateur, le COPF ou ta fédération d'affiliation.</span>
                  ) : (
                    <span className="italic">Aucune revendication en cours.</span>
                  )}
                </p>
              </div>
            </div>
          </SectionCard>

          {/* V13.7 — Validation officielle de la fiche par le COPF ou la Fédération */}
          <SectionCard
            title="Validation officielle de la fiche"
            description="Demande une validation officielle de ta fiche par le COPF ou ta fédération d'affiliation. Une fois validée, un badge « Fiche validée » s'affiche publiquement (site + PDF)."
          >
            <ProfileValidationBlock athlete={a} onChanged={(next) => setA(next)} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="sport" className="mt-4 space-y-4">
          <SectionCard title="Disciplines" description="Choisis tes disciplines. Clique pour ajouter/retirer.">
            <div className="flex flex-wrap gap-2">
              {disciplines.map((d) => {
                const active = (a.disciplines || []).includes(d.slug);
                return (
                  <button key={d.slug} data-testid={`edit-discipline-${d.slug}`} onClick={() => {
                    const list = active ? a.disciplines.filter((x) => x !== d.slug) : [...(a.disciplines || []), d.slug];
                    const main = a.main_discipline && list.includes(a.main_discipline) ? a.main_discipline : list[0] || null;
                    patch({ disciplines: list, main_discipline: main });
                  }} className={`px-3 py-1.5 rounded-full text-xs border ${active ? "bg-[hsl(var(--aito-lagoon))] text-black border-transparent" : "hover:bg-muted"}`}>
                    {d.name}
                  </button>
                );
              })}
              <CustomDisciplineButton athlete={a} onChange={setA} />
            </div>
            {a.disciplines?.length > 0 && (
              <div className="mt-3 text-xs">
                Discipline principale :{" "}
                <select className="ml-1 border rounded px-2 py-1 bg-background" value={a.main_discipline || ""} onChange={(e) => patch({ main_discipline: e.target.value || null })} data-testid="edit-main-discipline-select">
                  <option value="">—</option>
                  {a.disciplines.map((d) => <option key={d} value={d}>{disciplines.find((x) => x.slug === d)?.name || d}</option>)}
                </select>
              </div>
            )}
          </SectionCard>

          {(a.disciplines || []).length > 1 && (
            <SectionCard title="Ordre d'affichage des disciplines" description="L'ordre détermine l'importance affichée sur la fiche publique.">
              <ol className="space-y-1.5" data-testid="discipline-reorder">
                {((a.discipline_order && a.discipline_order.length) ? a.discipline_order : a.disciplines).map((slug, i, arr) => (
                  <li key={slug} className="flex items-center gap-2 p-2 border rounded-lg">
                    <span className="font-mono text-xs text-muted-foreground w-6">{i + 1}.</span>
                    <span className="flex-1">{disciplines.find((x) => x.slug === slug)?.name || slug}</span>
                    <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => { const order = [...arr]; [order[i - 1], order[i]] = [order[i], order[i - 1]]; patch({ discipline_order: order }); }} data-testid={`discipline-up-${slug}`}>↑</Button>
                    <Button size="sm" variant="ghost" disabled={i === arr.length - 1} onClick={() => { const order = [...arr]; [order[i], order[i + 1]] = [order[i + 1], order[i]]; patch({ discipline_order: order }); }} data-testid={`discipline-down-${slug}`}>↓</Button>
                  </li>
                ))}
              </ol>
            </SectionCard>
          )}
        </TabsContent>

        <TabsContent value="palmares" className="mt-4 space-y-4">
          <SectionCard
            title="Palmarès"
            description="Vos titres et distinctions. Les éléments masqués restent visibles ici et peuvent être réaffichés à tout moment. Utilise les cases à cocher pour modifier plusieurs résultats en une seule fois."
            action={
              !showAchForm && (
                <div className="flex gap-2">
                  <PalmaresAIOrganizer
                    athleteId={a.id}
                    discipline={a.main_discipline}
                    disciplines={disciplines}
                    onCommitted={(updated) => setA(updated)}
                  />
                  <Button size="sm" onClick={() => { setEditAchIndex(null); setShowAchForm(true); }} data-testid="edit-add-achievement">
                    <Plus className="h-4 w-4 mr-1" />Ajouter
                  </Button>
                </div>
              )
            }
          >
            {showAchForm && (
              <div className="mb-4">
                <AchievementInlineForm
                  athleteId={a.id}
                  disciplines={disciplines}
                  initialValue={editAchIndex !== null ? a.achievements[editAchIndex] : null}
                  editIndex={editAchIndex}
                  onSaved={(updated) => { setA(updated); closeAchForm(); }}
                  onCancel={closeAchForm}
                />
              </div>
            )}
            {(a.achievements || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">Aucun palmarès. Ajoutes-en pour valoriser ta fiche.</p>
            ) : (
              <>
                <PalmaresBulkActions
                  athlete={a}
                  selected={bulkSel}
                  setSelected={setBulkSel}
                  onApply={async (patch) => {
                    // Applique un patch sur les achievements sélectionnés
                    const indexes = [...bulkSel];
                    if (indexes.length === 0) return;
                    const updated = (a.achievements || []).map((ach, i) =>
                      indexes.includes(i) ? { ...ach, ...patch } : ach
                    );
                    try {
                      const { data } = await api.patch(`/athletes/${a.id}`, { achievements: updated });
                      setA(data);
                      setBulkSel(new Set());
                      toast.success(`${indexes.length} résultat(s) mis à jour.`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Échec de la mise à jour en masse.");
                    }
                  }}
                />
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  {a.achievements.map((ach, i) => {
                    const isHidden = !!ach.hidden;
                    const isChecked = bulkSel.has(i);
                    return (
                      <div key={i} className={`relative rounded-xl border transition ${isHidden ? "opacity-50 border-dashed" : ""} ${isChecked ? "ring-2 ring-[hsl(var(--aito-lagoon))]" : ""}`} data-testid={`achievement-row-${i}`}>
                        <MedalCard {...ach} />
                        {/* Case à cocher pour sélection multiple (bulk edit) */}
                        <div className="absolute left-2 top-2 bg-background/95 border rounded p-1 shadow-sm">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              const next = new Set(bulkSel);
                              if (next.has(i)) next.delete(i); else next.add(i);
                              setBulkSel(next);
                            }}
                            className="h-4 w-4 cursor-pointer"
                            data-testid={`bulk-select-${i}`}
                            aria-label={`Sélectionner ${ach.title || "ce résultat"}`}
                          />
                        </div>
                        {isHidden && (
                          <Badge variant="secondary" className="absolute top-2 left-10 gap-1 bg-background/90 border" data-testid={`achievement-hidden-badge-${i}`}>
                            <EyeOff className="h-3 w-3" />Masqué
                          </Badge>
                        )}
                        <div className="absolute right-2 top-2 flex gap-1">
                          <button
                            onClick={() => openAchEdit(i)}
                            title="Modifier"
                            data-testid={`edit-achievement-${i}`}
                            className="p-1.5 rounded bg-background/90 border hover:bg-[hsl(var(--aito-lagoon))] hover:text-black transition"
                          ><Pencil className="h-3.5 w-3.5" /></button>
                          <button
                            onClick={() => toggleAchHidden(i, isHidden)}
                            title={isHidden ? "Réafficher" : "Masquer"}
                            data-testid={`toggle-achievement-hidden-${i}`}
                            className="p-1.5 rounded bg-background/90 border hover:bg-muted transition"
                          >{isHidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}</button>
                          <button
                            onClick={() => removeAch(i)}
                            title="Supprimer"
                            data-testid={`remove-achievement-${i}`}
                            className="p-1.5 rounded bg-background/90 border hover:bg-destructive hover:text-white transition"
                          ><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </SectionCard>

          <SectionCard title="Résultats récents" description="Compétitions et classements." action={<Button size="sm" onClick={addResult} data-testid="edit-add-result"><Plus className="h-4 w-4 mr-1" />Ajouter</Button>}>
            {(a.results || []).length === 0 ? <p className="text-xs text-muted-foreground">Aucun résultat enregistré.</p> : (
              <div className="divide-y border rounded-lg">
                {a.results.map((r, i) => (
                  <div key={i} className="p-3 flex items-center justify-between gap-2">
                    <div className="min-w-0"><div className="font-display font-semibold text-sm truncate">{r.competition}</div><div className="text-xs text-muted-foreground">{[r.date, r.category].filter(Boolean).join(" · ")}</div></div>
                    <div className="font-mono text-sm">{r.result || (r.rank ? `#${r.rank}` : "—")}</div>
                    <button onClick={async () => { const { data } = await api.delete(`/athletes/${a.id}/list/results/${i}`); setA(data); }} data-testid={`remove-result-${i}`} className="p-1 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="network" className="mt-4 space-y-4">
          <SectionCard
            title="Liens validés (officiels)"
            description="Déclare un coach, un club ou une fédération existant·e dans l'annuaire. Tant que le lien n'est pas validé par l'entité cible (ou par la COPF), il reste privé."
          >
            <EntityLinksManager athleteId={a.id} />
          </SectionCard>
          <SectionCard title="Coachs (citations libres)" description="Liste libre de noms cités, utile pour les coachs qui n'ont pas encore de fiche officielle. Ces noms restent affichés à titre déclaratif uniquement."><StringListEditor items={a.coaches} kind="coach" athleteId={a.id} onChange={setA} placeholder="Nom du coach" /></SectionCard>
          <SectionCard title="Clubs (citations libres)" description="Idem — pour les clubs sans fiche officielle. Préfère le bloc « Liens validés » ci-dessus dès que le club existe."><StringListEditor items={a.clubs} kind="club" athleteId={a.id} onChange={setA} placeholder="Nom du club" /></SectionCard>
          <SectionCard title="Fédérations (citations libres)" description="Idem — pour les fédérations sans fiche officielle."><StringListEditor items={a.federations} kind="federation" athleteId={a.id} onChange={setA} placeholder="Nom de la fédération" /></SectionCard>
          <SectionCard
            title="Partenaires officiels (avec logo)"
            description="Mets tes partenaires en évidence avec leur logo, leur site et un niveau d'engagement (or / argent / bronze)."
          >
            <SponsorPartnersEditor
              items={a.sponsor_partners || []}
              athleteId={a.id}
              onChange={(updatedAthlete) => setA(updatedAthlete)}
            />
          </SectionCard>
          <SectionCard title="Soutiens additionnels (texte libre)" description="Pour les soutiens ne disposant pas encore d'une fiche officielle. Ils apparaissent en badges sous les partenaires."><StringListEditor items={a.sponsors} kind="sponsor" athleteId={a.id} onChange={setA} placeholder="Nom du sponsor" /></SectionCard>
        </TabsContent>

        <TabsContent value="media" className="mt-4 space-y-4">
          <SectionCard title="Photos" description="Vos photos officielles. Vérifiez bien les droits avant publication." action={<Button size="sm" onClick={addPhoto} data-testid="edit-add-photo"><Plus className="h-4 w-4 mr-1" />Ajouter</Button>}>
            {(a.photos || []).length === 0 ? <p className="text-xs text-muted-foreground">Aucune photo ajoutée.</p> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {a.photos.map((p, i) => (
                  <div key={p.id || i} className="relative group aspect-square overflow-hidden rounded-lg border">
                    <img src={p.url} alt={p.caption || ""} className="h-full w-full object-cover" />
                    <button onClick={async () => { const { data } = await api.delete(`/athletes/${a.id}/list/photos/${i}`); setA(data); }} className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded opacity-0 group-hover:opacity-100" data-testid={`remove-photo-${i}`}><Trash2 className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Vidéos" description="Liens YouTube, Facebook, Instagram, TikTok." action={<Button size="sm" onClick={addVideo} data-testid="edit-add-video"><Plus className="h-4 w-4 mr-1" />Ajouter</Button>}>
            {(a.videos || []).length === 0 ? <p className="text-xs text-muted-foreground">Aucune vidéo.</p> : (
              <ul className="space-y-2">
                {a.videos.map((v, i) => (
                  <li key={v.id || i} className="flex items-center gap-2 p-2 border rounded-lg">
                    <Radio className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
                    <a href={v.url} target="_blank" rel="noreferrer" className="flex-1 truncate text-sm hover:underline">{v.title || v.url}</a>
                    <Badge variant="secondary" className="capitalize">{v.platform || "lien"}</Badge>
                    <button onClick={async () => { const { data } = await api.delete(`/athletes/${a.id}/list/videos/${i}`); setA(data); }} data-testid={`remove-video-${i}`} className="p-1 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <SectionCard
            title="Articles & médias"
            description="Articles de presse, interviews, vidéos institutionnelles. Saisissez l'URL — les métadonnées (titre, résumé, miniature) sont récupérées automatiquement."
            action={!showArtForm && (<Button size="sm" onClick={() => { setEditArtIndex(null); setShowArtForm(true); }} data-testid="edit-add-article"><Plus className="h-4 w-4 mr-1" />Ajouter un article</Button>)}
          >
            {showArtForm && (
              <div className="mb-4">
                <ArticleInlineForm
                  athleteId={a.id}
                  disciplines={disciplines}
                  initialValue={editArtIndex !== null ? a.articles[editArtIndex] : null}
                  editIndex={editArtIndex}
                  onSaved={(updated) => { setA(updated); closeArtForm(); }}
                  onCancel={closeArtForm}
                />
              </div>
            )}
            {(a.articles || []).length === 0 ? <p className="text-xs text-muted-foreground">Aucun article ou média référencé.</p> : (
              <div className="space-y-3">
                {a.articles.map((art, i) => {
                  const isHidden = !!art.hidden;
                  const thumb = art.image_url || art.photo_url;
                  const kindLabel = (ARTICLE_KIND_OPTIONS.find((k) => k.value === art.kind)?.label) || "Article";
                  return (
                    <Card key={art.id || i} className={`p-3 flex items-start gap-3 transition ${isHidden ? "opacity-50 border-dashed" : ""}`} data-testid={`article-row-${i}`}>
                      <div className="h-20 w-32 shrink-0 rounded overflow-hidden bg-muted border flex items-center justify-center">
                        {thumb ? (
                          <img src={thumb} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.outerHTML = '<div class="h-full w-full flex items-center justify-center text-muted-foreground"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg></div>'; }} />
                        ) : (
                          (art.kind === "video") ? <Video className="h-6 w-6 text-muted-foreground" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5 mb-1">
                          <Badge variant="outline" className="capitalize text-[10px]">{kindLabel}</Badge>
                          {art.media && <Badge variant="secondary" className="text-[10px]">{art.media}</Badge>}
                          {art.date && <span className="text-[10px] text-muted-foreground">{art.date}</span>}
                          {isHidden && <Badge variant="secondary" className="gap-1 text-[10px]" data-testid={`article-hidden-badge-${i}`}><EyeOff className="h-3 w-3" />Masqué</Badge>}
                        </div>
                        <a href={art.url} target="_blank" rel="noreferrer" className="font-display font-semibold text-sm leading-tight hover:text-[hsl(var(--aito-lagoon))] line-clamp-2 block">{art.title}</a>
                        {(art.description || art.summary) && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{art.description || art.summary}</p>}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <button onClick={() => openArtEdit(i)} title="Modifier" data-testid={`edit-article-${i}`} className="p-1.5 rounded border hover:bg-[hsl(var(--aito-lagoon))] hover:text-black transition"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => toggleArtHidden(i, isHidden)} title={isHidden ? "Réafficher" : "Masquer"} data-testid={`toggle-article-hidden-${i}`} className="p-1.5 rounded border hover:bg-muted transition">{isHidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}</button>
                        <button onClick={() => removeArt(i)} title="Supprimer" data-testid={`remove-article-${i}`} className="p-1.5 rounded border hover:bg-destructive hover:text-white transition"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="monitor" className="mt-4 space-y-4">
          <SectionCard title="Liens sources cités" description="Les URLs déjà utilisées pour bâtir la fiche.">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <p className="text-xs text-muted-foreground flex-1 min-w-[200px]">
                {(a.sources || []).length === 0
                  ? "Aucune source n'est encore liée à cette fiche. Lance la recherche IA pour proposer des articles publics correspondant à cette identité."
                  : `${(a.sources || []).length} source(s) déjà liée(s). Tu peux en ajouter d'autres via la recherche IA.`}
              </p>
              <AISourceSearchButton
                entity={{
                  first_name: a.first_name || (a.full_name || "").split(" ")[0],
                  last_name: a.last_name || (a.full_name || "").split(" ").slice(1).join(" "),
                  nickname: a.nickname,
                  discipline: a.main_discipline,
                  club: a.club_name,
                  entity_type: "athlete",
                }}
                onAddSource={async (cand) => {
                  try {
                    const newSource = { url: cand.url, title: cand.title, kind: "article" };
                    const sources = [...(a.sources || []), newSource];
                    const { data } = await api.patch(`/athletes/${a.id}`, { sources });
                    setA(data);
                  } catch (e) { showError(e, { prefix: "Ajout source :" }); }
                }}
              />
            </div>
            {(a.sources || []).length === 0 ? null : (
              <ul className="text-sm space-y-1">
                {a.sources.map((s, i) => <li key={i}><a href={s.url} target="_blank" rel="noreferrer" className="text-[hsl(var(--aito-lagoon))] hover:underline">{s.title || s.url}</a></li>)}
              </ul>
            )}
          </SectionCard>

          <SectionCard title="Liens surveillés (veille IA)" description="L'IA peut scanner régulièrement ces liens et proposer de nouvelles informations — articles de presse comme fiches officielles de fédération (IBJJF, ISA, WSL, World Athletics, IJF, fédération française, régionale…). Sur une page officielle, elle extraira chaque résultat/palmarès ligne par ligne.">
            <StringListEditor items={a.monitored_links} kind="monitored_link" athleteId={a.id} onChange={setA} placeholder="https://..." />
            <div className="mt-4 space-y-3 pt-3 border-t">
              <label className="flex items-start gap-2 text-xs">
                <Checkbox checked={!!a.monitor_enabled} onCheckedChange={(v) => patch({ monitor_enabled: !!v })} data-testid="edit-monitor-toggle" />
                <span>Activer la veille automatique sur les liens surveillés.</span>
              </label>
              <div className="flex items-center gap-2 text-xs">
                <span>Fréquence :</span>
                <select className="border rounded px-2 py-1 bg-background" value={a.monitor_frequency || "manual"} onChange={(e) => patch({ monitor_frequency: e.target.value })} data-testid="edit-monitor-frequency">
                  <option value="daily">Quotidienne</option>
                  <option value="weekly">Hebdomadaire</option>
                  <option value="manual">Manuelle</option>
                  <option value="disabled">Désactivée</option>
                </select>
              </div>
              <Button size="sm" disabled={saving || !(a.monitored_links || []).length} onClick={async () => {
                setSaving(true);
                try { const { data } = await api.post(`/ai/athletes/${a.id}/monitor/run`); toast.success(`${data.appended} suggestion(s) détectée(s).`); const f = await api.get(`/athletes/${a.id}`); setA(f.data); }
                catch (e) { showError(e); }
                finally { setSaving(false); }
              }} data-testid="edit-monitor-run" className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"><Sparkles className="h-4 w-4 mr-1.5" />Lancer la veille maintenant</Button>
            </div>
          </SectionCard>

          <SectionCard title="Assistant de veille personnelle (recherche web)" description="L'IA effectue des recherches publiques sur votre nom. Aucune publication automatique.">
            <label className="flex items-start gap-2 text-xs mb-3">
              <Checkbox checked={!!a.web_search_enabled} onCheckedChange={(v) => patch({ web_search_enabled: !!v })} data-testid="edit-websearch-toggle" />
              <span>J'autorise AITO.PF à effectuer des recherches automatisées sur les informations sportives publiques me concernant afin de me proposer des mises à jour de mon profil. Cette option est désactivable à tout moment.</span>
            </label>
            <Button size="sm" disabled={!a.web_search_enabled || saving} onClick={async () => {
              setSaving(true);
              try { const { data } = await api.post(`/ai/athletes/${a.id}/web-search-veille`, {}); toast.success(`${data.appended || 0} suggestion(s) détectée(s) sur le web.`); const f = await api.get(`/athletes/${a.id}`); setA(f.data); }
              catch (e) { showError(e); }
              finally { setSaving(false); }
            }} data-testid="edit-websearch-run"><Sparkles className="h-4 w-4 mr-1.5" />Lancer une recherche web</Button>
          </SectionCard>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <SectionCard title="Historique des modifications" description="Les 50 dernières modifications de votre fiche.">
            {(a.modification_history || []).length === 0 ? <p className="text-xs text-muted-foreground">Aucune modification enregistrée.</p> : (
              <ul className="divide-y border rounded-lg text-sm">
                {[...a.modification_history].reverse().slice(0, 50).map((h, i) => (
                  <li key={i} className="p-3 flex items-center justify-between">
                    <div className="font-mono text-xs text-muted-foreground">{new Date(h.at).toLocaleString("fr-FR")}</div>
                    <div className="flex flex-wrap gap-1 justify-end">{(h.fields_changed || []).map((f, j) => <Badge key={j} variant="secondary">{f}</Badge>)}</div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>

      {saving && <div className="fixed bottom-4 right-4 bg-black text-white text-xs px-3 py-1.5 rounded-full"><Save className="inline h-3 w-3 mr-1" />Enregistrement…</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  V11 — GamesSelectionBlock : déclaration Athlete → PENDING (jamais public tant que non validé)
// ---------------------------------------------------------------------------
function GamesSelectionBlock({ athleteId, mainDiscipline }) {
  const [state, setState] = React.useState({ loading: true, selection: null, busy: false });
  const [discipline, setDiscipline] = React.useState("");

  const refresh = React.useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/games-selections/athlete/${athleteId}`);
      setState({ loading: false, selection: data.selection, busy: false });
      setDiscipline(data.selection?.discipline || mainDiscipline || "");
    } catch (_e) {
      setState({ loading: false, selection: null, busy: false });
    }
  }, [athleteId, mainDiscipline]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const declare = async () => {
    setState((s) => ({ ...s, busy: true }));
    try {
      const { toast } = await import("sonner");
      await api.post(`/games-selections/athlete/${athleteId}/declare`, { discipline: discipline || null });
      toast.success("Sélection déclarée — en attente de validation AITO / COPF.");
      await refresh();
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e?.response?.data?.detail || "Impossible de déclarer la sélection.");
      setState((s) => ({ ...s, busy: false }));
    }
  };

  if (state.loading) {
    return <div className="text-xs text-muted-foreground" data-testid="games-selection-loading">Chargement…</div>;
  }

  const sel = state.selection;
  const status = sel?.status;

  // Aucune sélection → case à cocher de déclaration
  if (!sel || status === "rejected" || status === "revoked") {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 rounded-md border bg-[hsl(var(--aito-gold)/0.05)]">
          <input
            id="games-selection-toggle"
            data-testid="games-selection-declare-checkbox"
            type="checkbox"
            checked={false}
            onChange={() => declare()}
            disabled={state.busy}
            className="h-4 w-4 mt-1 accent-[hsl(var(--aito-gold))] shrink-0"
          />
          <div className="flex-1">
            <label htmlFor="games-selection-toggle" className="text-sm font-medium cursor-pointer">
              Je suis sélectionné·e aux Jeux du Pacifique 2027
            </label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Coche cette case pour <strong>déclarer</strong> ta sélection. Elle passera en attente jusqu'à validation officielle par AITO / COPF. Tu n'apparaîtras pas publiquement tant que la validation n'a pas eu lieu.
            </p>
            {status === "rejected" && (
              <p className="text-[11px] text-destructive mt-1">
                ⚠ Une précédente déclaration a été refusée{sel.rejected_reason ? ` : « ${sel.rejected_reason} »` : ""}. Tu peux redéclarer si nécessaire.
              </p>
            )}
            {status === "revoked" && (
              <p className="text-[11px] text-amber-700 mt-1">
                ⚠ Ta sélection a été révoquée{sel.revoked_reason ? ` : « ${sel.revoked_reason} »` : ""}. Tu peux redéclarer si tu es à nouveau concerné·e.
              </p>
            )}
          </div>
        </div>
        <div>
          <label className="text-xs">Discipline visée (optionnel)</label>
          <Input
            value={discipline}
            onChange={(e) => setDiscipline(e.target.value)}
            placeholder={mainDiscipline || "va'a, judo, athlétisme…"}
            data-testid="games-selection-discipline-input"
          />
        </div>
      </div>
    );
  }

  // PENDING
  if (status === "pending") {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3" data-testid="games-selection-pending">
        <div className="flex items-center gap-2 font-medium text-amber-900 dark:text-amber-200">
          <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
          Sélection déclarée — en attente de validation
        </div>
        <p className="text-[11px] text-amber-800 dark:text-amber-300 mt-1">
          Ta déclaration a bien été enregistrée le {sel.declared_at ? new Date(sel.declared_at).toLocaleDateString("fr-FR") : "—"}.
          {sel.discipline && <> Discipline déclarée : <strong className="capitalize">{sel.discipline}</strong>.</>} Un·e admin ou la COPF va la valider prochainement.
          Tu n'apparais pas encore dans la délégation Polynésie publique.
        </p>
      </div>
    );
  }

  // SELECTED
  return (
    <div className="rounded-md border border-[hsl(var(--aito-gold))]/50 bg-[hsl(var(--aito-gold))]/10 p-3" data-testid="games-selection-selected">
      <div className="flex items-center gap-2 font-medium">
        <span className="inline-block h-2 w-2 rounded-full bg-[hsl(var(--aito-gold))]" />
        Sélection officielle validée — Jeux du Pacifique 2027
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">
        Ta sélection a été validée le {sel.validated_at ? new Date(sel.validated_at).toLocaleDateString("fr-FR") : "—"}.
        {sel.discipline && <> Discipline : <strong className="capitalize">{sel.discipline}</strong>.</>} Tu apparais dans la délégation Polynésie publique.
        Une révocation ne peut être faite que par AITO / COPF.
      </p>
    </div>
  );
}

